import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, Observable } from 'rxjs';

export enum VerificationStatus {
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  FAILURE = 'FAILURE'
}

export interface VerificationResponse {
  status: VerificationStatus;
  message?: string;
  finalStatus?: boolean;
  isTimeout?: boolean;
}

export interface VerificationDialogData {
  // Required: Polling function that returns verification status
  pollFunction: () => Observable<VerificationResponse>;
  
  // Display messages
  title: string;
  message: string;
  checkingMessage?: string;
  successMessage: string;
  errorMessage: string;
  
  // Configuration
  pollingInterval?: number; // in milliseconds, default: 3000
  maxAttempts?: number; // maximum polling attempts, default: 60 (3 minutes with 3s interval)
  showRetryButton?: boolean; // default: true
  
  // Optional: Window reference to close on completion
  windowToClose?: Window | null;
  
  // Optional: Custom waiting message based on estimated time
  waitingMessage?: string;
}

@Component({
  selector: 'app-verification-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title class="perse-dialog-title">{{ data.title }}</h2>
    <mat-dialog-content>
      <div class="progress-container">
        <p>{{ data.message }}</p>
        <p>{{ statusMessage }}</p>
        <p *ngIf="!verificationComplete && data.waitingMessage">
          {{ data.waitingMessage }}
        </p>
        <mat-progress-bar *ngIf="!verificationComplete" mode="indeterminate"></mat-progress-bar>
        <div *ngIf="verificationComplete" class="verification-result">
          <p [ngClass]="{'success': verificationSuccess, 'error': !verificationSuccess}">
            <mat-icon class="icon-no-shrink">{{ verificationSuccess ? 'check_circle' : 'error' }}</mat-icon>
            {{ verificationSuccess ? data.successMessage : data.errorMessage }}
          </p>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button *ngIf="verificationComplete" mat-button [mat-dialog-close]="{ status: verificationSuccess ? 'SUCCESS' : 'FAILURE' }">OK</button>
      <button *ngIf="verificationComplete && !verificationSuccess && data.showRetryButton !== false" 
              mat-button color="primary" 
              [mat-dialog-close]="{ status: 'FAILURE'}">Try Again</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .progress-container {
      min-width: 300px;
      padding: 10px 0;
    }
    mat-progress-bar {
      margin: 20px 0;
    }
    .verification-result {
      margin-top: 20px;
      font-weight: 500;
    }
    .success {
      color: green;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .error {
      color: red;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    mat-icon {
      vertical-align: middle;
    }
  `]
})
export class VerificationDialogComponent implements OnInit, OnDestroy {
  verificationComplete: boolean = false;
  verificationSuccess: boolean = false;
  statusMessage: string = "";
  private statusSubscription: Subscription | null = null;
  private attempts: number = 0;
  private readonly pollingInterval: number;
  private readonly maxAttempts: number;

  constructor(
    private dialogRef: MatDialogRef<VerificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VerificationDialogData
  ) {
    this.pollingInterval = data.pollingInterval || 3000; // Default 3 seconds
    this.maxAttempts = data.maxAttempts || 60; // Default 3 minutes
    this.statusMessage = data.checkingMessage || "Starting verification process...";
  }

  ngOnInit(): void {
    this.startVerification();
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  private startVerification(): void {
    this.attempts = 0;
    // Add a 2-second delay before starting the first poll
    setTimeout(() => {
      this.pollStatus();
    }, 2000);
  }

  private pollStatus(): void {
    if (this.attempts >= this.maxAttempts) {
      this.statusMessage = 'Verification timed out. Please try again or try with a different consent method.';
      this.verificationComplete = true;
      this.verificationSuccess = false;
      this.closeWindow();
      return;
    }

    this.attempts++;
    
    this.statusSubscription = this.data.pollFunction().subscribe({
      next: (response: VerificationResponse) => {
        this.handleVerificationResponse(response);
      },
      error: (error: any) => {
        console.error('Error during verification polling:', error);
        this.statusMessage = 'Error during verification. Please try again.';
        this.verificationComplete = true;
        this.verificationSuccess = false;
        this.closeWindow();
      }
    });
  }

  private handleVerificationResponse(response: VerificationResponse): void {
    // Update status message if provided
    if (response.message) {
      this.statusMessage = response.message;
    }

    switch (response.status) {
      case VerificationStatus.SUCCESS:
        this.verificationComplete = true;
        this.verificationSuccess = true;
        this.closeWindow();
        setTimeout(() => {
          this.dialogRef.close({ status: 'SUCCESS' });
        }, 1000); // Close dialog after 3 seconds to show success message
        break;

      case VerificationStatus.ERROR:
      case VerificationStatus.FAILURE:
        this.verificationComplete = true;
        this.verificationSuccess = false;
        this.closeWindow();
        break;

      case VerificationStatus.PENDING:
        if (response.finalStatus) {
          // Final status but still pending means timeout or final failure
          this.verificationComplete = true;
          this.verificationSuccess = false;
          this.closeWindow();
        } else {
          // Continue polling after delay
          setTimeout(() => {
            if (!this.verificationComplete) {
              this.pollStatus();
            }
          }, this.pollingInterval);
        }
        break;

      default:
        console.warn('Unknown verification status:', response.status);
        // Continue polling for unknown status
        setTimeout(() => {
          if (!this.verificationComplete) {
            this.pollStatus();
          }
        }, this.pollingInterval);
        break;
    }
  }

  private closeWindow(): void {
    if (this.data.windowToClose) {
      setTimeout(() => {
        this.data.windowToClose?.close();
      }, 3000);
    }
  }
}
